/**
 * Identifies a group of features by selecting them.
 * @module feature-selection-tool
 * @see {@link http://wiki.infor.com:8080/confluence/label/~CALIX/feature-selection-tool|blog}
 */

import _ = require("underscore");
import ol = require("ol");
import Utils = require("../utils");
import geo = require("../utils/geo");
import Portal = require("portal-gis");
import Converter = require("../utils/ol3/converter");
import Base = require("./radio-command");
import Symbology = require("../utils/symbology");
import MapView = require("../views/MapView");

interface OutArgs {
    layers: Rhythm.GIS.FeatureQueryResultLayer[]; // should probably leave these as ol.layers
    pixel?: ol.Pixel;
    point?: ol.Coordinate;
    box?: ol.Extent;
};

let conditionMap = {
    'always': ol.events.condition.always,
    'alt': ol.events.condition.altKeyOnly,
    'ctrl': ol.events.condition.platformModifierKeyOnly,
    'shift': ol.events.condition.shiftKeyOnly,
    'alt+shift': ol.events.condition.altShiftKeysOnly,
    'never': ol.events.condition.never,
    'none': ol.events.condition.noModifierKeys,
};

class Tool extends Base<{
    symbology: string;
    type: string;
}> {

    static about = `
Identifies a group of features by selecting them.

Usage Example:

\`\`\`xml
<COMMAND about="Allow user to click a feature to display information about that feature; this is failing because the map is not loaded by the time this runs so moving it into a toolbar control" disabled="False" id="get-info-tool" mid="app/commands/feature-selection-tool" text="Get Info" type="startup">
  <OPTIONS>
    <Values about="User can click the map to query feature info (set value to point+box for multi-select)" id="type" value="point" />
    <Values about="Identifies the desired EVENT handler" id="event" value="show-info" />
    <Values about="Make the buffer area large enough to easily select point features" id="buffer-size" value="16" />
    <Values id="condition" value="none" />
    <Values about="Ignore the green and red zones when searching for existing features" id="exclude-layer" value="green-zone,red-zone" />
  </OPTIONS>
</COMMAND>    
\`\`\`

This module has the job of searching the map for features within an extent.  
It can do this by "box", "point" or "box+point" interactions.  
A "point" interaction is just a small "box" interaction.  
The size is calculated in pixels and the pixel buffer size is configured 
with the "buffer-size" option.  
It selects all the features and signals an event.

Here is another usage example providing a "filter" to include only addresses (AddrType = 'A'):

\`\`\`xml
<COMMAND mid="app/commands/feature-selection-tool" about="Allow user ability to select addresses without selecting any other types of features" text="Select Addresses" id="select-address-only" type="action">
 <OPTIONS>
 <Values value="box" id="type" />
 <Values about="Provide a name for handler to listen on" value="address-selection" id="event" />
 <Values value="geo-selection" id="radio-group" />
 </OPTIONS>
</COMMAND>
\`\`\`

In this case, the event is "address-selection".  
The purpose of this command is to select addresses and nothing else. 
 he event listening to this command does the actual work:

\`\`\`xml
<EVENT event="address-selection" mid="app/commands/feature-selector" text="Select Addresses" disabled="False" id="ips-address-selector">
 <OPTIONS>
 <Values about="Select addresses from the address test layer" value="{&quot;moniker&quot;: &quot;Hansen.Property.Address&quot;}" id="filter" />
 </OPTIONS>
</EVENT>
\`\`\`

    `;

    static options = {
        "condition": {
            about: "The click event should be accompanied by what keyboard condition?",
            domain: "always|alt|ctrl|shift|alt+shift|never|none",
            default: "none"
        },
        "symbology": {
            about: "Symbology to use when rendering the selection box",
            default: "selection-box"
        },
        "type": {
            about: "identifies how a feature can be selected",
            domain: "default|point|box|point+box",
            default: "default"
        },
    }

    private map: ol.Map;
    private mapView: MapView;

    static input = {
        'exclude-layer': 'comma-separated list of layers to ignore'
    };

    go() {

        let options = this.asOptions(Tool.options);

        let selectionType = options["type"];

        this.mediator.mapViewMap(v => {
            this.mediator.mapView(v => this.mapView = v);
            this.map = v;


            switch (selectionType) {

                case "point": {
                    // a DragBox interaction used to select features by drawing boxes
                    let h = this.createPointInteraction();
                    this.stop = () => h.stop();
                    break;
                }

                case "box": {
                    // a DragBox interaction used to select features by drawing boxes
                    let h = this.createBoxInteraction();
                    this.stop = () => h.stop();
                    break;
                }

                case "default":
                case "point+box":
                case "box+point":
                case "point,box":
                case "box,point": {
                    let hBox = this.createBoxInteraction();
                    let hSelect = this.createPointInteraction();
                    this.stop = () => {
                        hBox.stop();
                        hSelect.stop();
                    };

                    break;

                }

                // box & point
                default:
                    console.warn("unsupported selection type");
                    break;
            }

        });
    }

    private createPointInteraction() {
        /* why not this?
            var select = new ol.interaction.Select();
            map.addInteraction(select); 
        */
        let map = this.map;

        let bufferSize = 8;
        Utils.ifOption(this.options, "buffer-size", v => bufferSize = parseInt(v, 10));

        let h = map.on("click", evt => {

            let condition = conditionMap[this.options["condition"]] || conditionMap.none;
            if (!condition(evt)) return;

            let layerHash = <Array<Rhythm.GIS.FeatureQueryResultLayer>>[];

            // ol3 features are converted to a 'standard' feature which looks more like an ags feature
            // and is the format all handlers expect
            let pixel = <ol.Pixel>evt.pixel;
            let box = geo.pixelToExtent(map, pixel, bufferSize);

            let outArgs = this.asOutArgs({
                pixel: pixel,
                box: box
            });

            this.trigger(outArgs);

        });

        return {
            stop: () => map.unByKey(h)
        };
    }

    private createBoxInteraction() {
        let map = this.map;

        let style = Symbology.findStyle(this.mediator, this.options["symbology"]);

        let condition = ol.events.condition.always;
        if (this.options["condition"]) {
            condition = conditionMap[this.options["condition"]];
            if (!condition) {
                console.error("invalid condition");
                condition = ol.events.condition.always;
            }
        }

        let dragBox = new ol.interaction.DragBox({
            condition: condition,
            style: style
        });

        map.addInteraction(dragBox);

        let h = dragBox.on('boxend', evt => {
            let geom = dragBox.getGeometry();
            let extent = geom.getExtent();
            this.trigger(this.asOutArgs({
                extent: extent
            }));

        });

        return {
            stop: () => {
                map.removeInteraction(dragBox);
                map.unByKey(h);
            }
        };

    }

    // no way to get the layer of a feature?    
    private createSelectInteraction() {
        let map = this.map;
        let select = new ol.interaction.Select();
        let converter = new Converter(map.getView().getProjection().getCode());

        map.addInteraction(select);

        let h = select.on("select", (evt: {
            selected: Array<ol.Feature>;
            deselected: Array<ol.Feature>;
        }) => {

            let features = evt.selected; //select.getFeatures();

            // spoof a query result container
            let l = this.mapView.getLayers().filter(l => l.get("id") === "civics")[0];
            let layer = <Rhythm.GIS.FeatureQueryResultLayer>{
                _extensions_: Utils.getExtensionKey(l, true),
                features: converter.fromNativeFeatures(features).features
            };

            let outArgs = <OutArgs>{ layers: [layer] };

            this.trigger(outArgs);

        });

        return {
            stop: () => {
                map.removeInteraction(select);
                map.unByKey(h);
            }
        };

    }

    private asOutArgs(args: {
        box?: number[];
        extent?: ol.Extent;
        pixel?: ol.Pixel;
    }) {
        let map = this.map;

        let wkid = map.getView().getProjection().getCode();

        let layers = this.getFeatures(args);

        if (args.extent) {
            let rhythmExt = <any>geo.transformExtent("EPSG:4326", {
                geometry: args.extent,
                spatialReference: {
                    wkid: wkid
                }
            });
            return <OutArgs>{
                layers: layers,
                box: rhythmExt.geometry,
                pixel: args.pixel
            };
        }

        else if (args.box) {
            return <OutArgs>{
                layers: layers,
                box: args.box,
                pixel: args.pixel
            };
        }

        else if (args.pixel) {
            return <OutArgs>{
                layers: layers,
                pixel: args.pixel
            };
        }

        console.warn("expecting extent or pixel");
        return { layers: [] };
    }

    /** searches all visible layers for features within the extent */
    private getFeatures(args: {
        extent?: ol.Extent;
        pixel?: ol.Pixel;
    }) {
        let map = this.map;
        // get features within extent!
        let converter = new Converter(map.getView().getProjection().getCode());

        let exclusionLayerIds = Utils.split(this.findOption("exclude-layer") || "");

        if (args.pixel) {
            let layers: Rhythm.GIS.FeatureQueryResultLayer[] = [];
            let activeLayer: Rhythm.GIS.FeatureQueryResultLayer;
            let maxFeatureCount = parseInt(this.findOption("max-feature-count") || "-1");
            let count = 0;

            map.forEachFeatureAtPixel(args.pixel, (feature, layer) => {
                if (!layer) return;
                if (0 < maxFeatureCount && maxFeatureCount <= count) return true;
                if (exclusionLayerIds.length && 0 <= exclusionLayerIds.indexOf(layer.get("id"))) return;

                let symbology = layer.get("symbology");
                let key = this.mediator.extensions.getExtensionKey(layer, true);
                if (!activeLayer || Utils.getExtensionKey(activeLayer) !== key) {
                    activeLayer = Utils.first(layers, l => this.mediator.extensions.getExtensionKey(activeLayer) === key);
                    if (!activeLayer) {
                        activeLayer = { features: [] };
                        layers.push(activeLayer);
                    }
                    this.mediator.extensions.bind(activeLayer, layer);
                }
                // is cluster?
                let features = feature.get("features") || [feature];

                features.forEach(f => activeLayer.features.push(converter.fromNativeFeatures([f]).features[0]));

                // layer symbology is the default symbology for features
                symbology && activeLayer.features
                    .filter(f => !f.attributes["symbology"])
                    .forEach(f => f.attributes["symbology"] = symbology);

                count++;
            });

            return layers;
        }

        if (args.extent) {
            let layers = this.mapView.getLayers()
                .filter(l => l instanceof ol.layer.Vector)
                .map(l => <ol.layer.Vector>l)
                .filter(l => l.getVisible())
                .map(l => {
                    // spoof a query result container
                    let layer = <Rhythm.GIS.FeatureQueryResultLayer>{
                        _extensions_: Utils.getExtensionKey(l, true),
                        features: []
                    };

                    // spoof query results
                    let source = l.getSource();

                    if (args.extent) {
                        let features = source.getFeaturesInExtent(args.extent);
                        layer.features = layer.features.concat(converter.fromNativeFeatures(features).features);
                    }
                    return layer;
                });

            return layers;
        }
    }
}

export = Tool;